/*
 * Description:
 * ---------------------
 * Device Speed Limits: a speed limit for one device, or for a group of them,
 * on top of the router's own settings (cloud-anchor/pkg/devicectl).
 *
 * A device is a unique hardware address. It joins a profile, and a device in
 * no profile uses the router's settings, which is every device until someone
 * decides otherwise. One profile can be the one new devices join, which is how
 * a walk-in guest gets a limit without being enrolled. Whether a limit is in
 * place is the appliance's own reading of the kernel, never an assumption from
 * having asked. All wording lives in deviceWords.js.
 *
 * ! file components/DeviceLimitsPanel.js
 */
import React, { useMemo, useState } from "react";
import { FiAlertTriangle, FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";
import useDeviceControls from "../hooks/useDeviceControls";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { FEATURE_ABSENT, FEATURE_UNREACHABLE } from "../hooks/useEdgeFeature";
import FeatureUnavailableNotice from "./FeatureUnavailableNotice";
import { useConfirm } from "./ConfirmModal";
import { useWanName, WanLabel } from "./WanNames";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import { fmtBytes } from "../utils/formatters";
import * as W from "./deviceWords";
import * as FW from "./firewallWords";
import "./DeviceLimits.css";

/* The two limit inputs, blank meaning no limit. Shared with the device
   drawer's own-limit form so the two read the same. */
export function LimitFields({ down, up, onChange, min = W.CAP_FLOOR_MBIT, max = W.CAP_CEILING_MBIT, dark = false }) {
  const field = (key, label, value) => (
    <label className="dl-field">
      <span>{label}</span>
      <input
        className={dark ? "dl-input dark" : "dl-input"}
        type="number" min={min} max={max} step="any" inputMode="decimal"
        placeholder="No limit" value={value}
        onChange={(e) => onChange(key, e.target.value)}
        aria-label={`${label} in Mbit/s`}
      />
      <span className="dl-unit">Mbit/s</span>
    </label>
  );
  return (
    <div className="dl-fields">
      {field("down", "Download", down)}
      {field("up", "Upload", up)}
    </div>
  );
}

/* A form's text back to the numbers the edge takes: blank is no limit. */
export function limitsFromFields(down, up) {
  const n = (v) => (String(v).trim() === "" ? 0 : Number(v));
  return { down_mbit: n(down), up_mbit: n(up) };
}

/* Lines and combining, for an edge that says whether it can apply them. One on
   an older build says nothing, and gets neither control. */
function LineFields({ wans, tunnel, circuits, setCircuits, bonding, setBonding }) {
  const toggle = (iface) =>
    setCircuits(circuits.includes(iface) ? circuits.filter((c) => c !== iface) : [...circuits, iface]);
  return (
    <>
      <fieldset className="dl-lines">
        <legend>{W.LINES.title}</legend>
        {!tunnel && <p className="dl-note">{W.LINES.direct}</p>}
        <label className="dl-check">
          <input type="checkbox" checked={circuits.length === 0} onChange={() => setCircuits([])} /> {W.LINES.all}
        </label>
        {wans.map((iface) => (
          <label key={iface} className="dl-check">
            <input type="checkbox" checked={circuits.includes(iface)} onChange={() => toggle(iface)} /> <WanLabel iface={iface} />
          </label>
        ))}
        {circuits.length > 0 && <p className="dl-note">{W.LINES.permission}</p>}
      </fieldset>
      <label className="dl-field">
        <span>Combine lines</span>
        <select className="dl-input" value={bonding} aria-label="Combine lines" onChange={(e) => setBonding(e.target.value)}>
          {W.BONDING_CHOICES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
    </>
  );
}

/* The profile's exceptions: one kind of traffic at a time, with its own lines
   and combining. Offered only where LineFields is, since it is the same
   setting at a finer grain. */
function ClassFields({ classes, setClasses, wans, circuits, classList, blocked }) {
  const labelFor = (id) => classList.find((c) => c.key === id)?.label || id;
  const offered = circuits.length ? circuits : wans;
  const unused = classList.filter((c) => !(c.key in classes) && !(c.key in blocked));
  const set = (id, row) => setClasses({ ...classes, [id]: row });
  const remove = (id) => {
    const next = { ...classes };
    delete next[id];
    setClasses(next);
  };
  const toggle = (id, row, iface) => {
    const on = row.circuits || [];
    set(id, { ...row, circuits: on.includes(iface) ? on.filter((c) => c !== iface) : [...on, iface] });
  };
  return (
    <fieldset className="dl-lines">
      <legend>{W.CLASSES.title}</legend>
      {Object.keys(classes).length === 0 && <p className="dl-note">{W.CLASSES.none}</p>}
      {Object.entries(classes).map(([id, row]) => (
        <div key={id} className="dl-class">
          <span className="dl-class-name">{labelFor(id)}</span>
          {offered.map((iface) => (
            <label key={iface} className="dl-check">
              <input type="checkbox" checked={(row.circuits || []).includes(iface)}
                onChange={() => toggle(id, row, iface)} /> <WanLabel iface={iface} />
            </label>
          ))}
          <select className="dl-input narrow" value={row.bonding || ""}
            aria-label={`Combine lines for ${labelFor(id)}`}
            onChange={(e) => set(id, { ...row, bonding: e.target.value })}>
            {W.BONDING_CHOICES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
          <button type="button" className="dl-btn icon danger" aria-label={`Remove ${labelFor(id)}`}
            onClick={() => remove(id)}><FiTrash2 size={14} /></button>
        </div>
      ))}
      {unused.length > 0 && (
        <select className="dl-input" value="" aria-label={W.CLASSES.add}
          onChange={(e) => e.target.value && set(e.target.value, { circuits: [], bonding: "" })}>
          <option value="">{W.CLASSES.add}</option>
          {unused.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
      )}
      {circuits.length > 0 && <p className="dl-note">{W.CLASSES.within}</p>}
    </fieldset>
  );
}

function ProfileForm({ profile, bounds, busy, onSave, onCancel, lines, classList }) {
  const [name, setName] = useState(profile?.name || "");
  const [down, setDown] = useState(profile?.down_mbit ? String(profile.down_mbit) : "");
  const [up, setUp] = useState(profile?.up_mbit ? String(profile.up_mbit) : "");
  const [circuits, setCircuits] = useState(profile?.circuits || []);
  const [bonding, setBonding] = useState(profile?.bonding || "");
  const [classes, setClasses] = useState(() => W.lineClasses(profile?.classes));
  // What the Firewall screen sets on a profile, its destinations and the
  // kinds of traffic it blocks, goes back as it came.
  const blocked = W.blockedClasses(profile?.classes);
  const submit = (e) => {
    e.preventDefault();
    const kept = { ...(profile || {}) };
    delete kept.classes;
    const named = { ...W.filledClasses(classes), ...blocked };
    const extra = lines
      ? { circuits, bonding, ...(Object.keys(named).length ? { classes: named } : {}) }
      : (profile?.classes ? { classes: profile.classes } : {});
    onSave({ ...kept, name, ...limitsFromFields(down, up), ...extra });
  };
  return (
    <form className="dl-form" onSubmit={submit}>
      <label className="dl-field wide">
        <span>Name</span>
        <input className="dl-input" value={name} maxLength={W.MAX_PROFILE_NAME}
          placeholder="For example Guests" onChange={(e) => setName(e.target.value)} />
      </label>
      <LimitFields down={down} up={up} min={bounds.min} max={bounds.max}
        onChange={(k, v) => (k === "down" ? setDown(v) : setUp(v))} />
      <p className="dl-note">{W.PER_DEVICE}</p>
      {lines && (
        <>
          <LineFields {...lines} circuits={circuits} setCircuits={setCircuits} bonding={bonding} setBonding={setBonding} />
          <ClassFields {...lines} circuits={circuits} classes={classes} setClasses={setClasses} classList={classList} blocked={blocked} />
        </>
      )}
      <div className="dl-actions">
        <button type="submit" className="dl-btn primary" disabled={busy || !name.trim()}>Save</button>
        <button type="button" className="dl-btn" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </form>
  );
}

export default function DeviceLimitsPanel({ selectedNode, trafficClasses }) {
  const dc = useDeviceControls(selectedNode);
  const confirm = useConfirm();
  const wanName = useWanName();
  const [status, setStatus] = useState(null);
  const [lan, setLan] = useState([]);
  const [wans, setWans] = useState([]);
  const [editing, setEditing] = useState(null); // a profile id, "new", or null
  const [adding, setAdding] = useState({ mac: "", profile: "" });
  const view = dc.view;
  const live = !!selectedNode && !!view?.available;
  const classList = useMemo(() => trafficClasses || [], [trafficClasses]);
  const labelFor = (id) => classList.find((c) => c.key === id)?.label || id;

  useVisibleInterval(async () => {
    const url = nodeApiUrlFor(selectedNode);
    const read = (path) => fetchWithTimeout(url(path)).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    const [s, d, w] = await Promise.all([read("/devices/controls/status"), read("/diagnostics/devices"), read("/wans")]);
    if (s) setStatus(s.status || null);
    if (d) setLan(d.devices || []);
    if (w) setWans((w.wans || []).map((x) => x.name));
  }, 10000, live);

  const names = useMemo(() => {
    const m = {};
    for (const d of lan) {
      const mac = W.normalizeMac(d.mac);
      if (mac) m[mac] = d.hostname && d.hostname !== "*" ? d.hostname : d.ip;
    }
    return m;
  }, [lan]);

  if (!selectedNode) {
    return <div className="panel"><div className="panel-sub">Select a node to set its devices' speed limits.</div></div>;
  }
  if (dc.state === FEATURE_ABSENT) {
    return (
      <FeatureUnavailableNotice nodeId={selectedNode} feature="Device speed limits"
        detail="This appliance is running a build from before device speed limits shipped. Once it is
                updated, this screen fills in on its own, and nothing else on the box is affected." />
    );
  }
  if (dc.state === FEATURE_UNREACHABLE) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Could not read device speed limits from {selectedNode}</div>
        <div className="panel-sub">The request did not come back. The screen keeps trying and fills in on its own.</div>
      </div>
    );
  }
  if (!view) {
    return <div className="panel"><div className="panel-sub">Reading device speed limits…</div></div>;
  }
  if (!view.available) {
    return (
      <div className="panel dl-warn">
        <div className="panel-title"><FiAlertTriangle size={17} /> Device speed limits cannot run on this appliance</div>
        <div className="panel-sub">{view.reason}</div>
      </div>
    );
  }

  const bounds = { min: view.min_cap_mbit, max: view.max_cap_mbit };
  const lines = typeof view.tunnel === "boolean" ? { wans, tunnel: view.tunnel } : null;
  const profiles = view.profiles || [];
  const byId = Object.fromEntries(profiles.map((p) => [p.id, p]));
  const entries = Object.fromEntries((status?.devices || []).map((e) => [e.mac, e]));
  const members = Object.entries(view.devices || {}).map(([mac, a]) => ({ mac, profile: a.profile, joined: false }));
  for (const e of status?.devices || []) {
    if (e.joined) members.push({ mac: e.mac, profile: e.profile, joined: true });
  }
  const counts = {};
  for (const m of members) counts[m.profile] = (counts[m.profile] || 0) + 1;
  const unassigned = lan.filter((d) => {
    const mac = W.normalizeMac(d.mac);
    return mac && !(view.devices || {})[mac];
  });

  const save = async (p) => {
    if (await dc.saveProfile(p)) setEditing(null);
  };
  // A profile holding its devices to some places holds every new device to
  // them, the operator's own computer included if it has no profile.
  const joinNew = async (id) => {
    if ((byId[id]?.destinations || []).length && !(await confirm(FW.NEW_DEVICES_ASK))) return;
    dc.setNewDevices(id);
  };
  const remove = async (p) => {
    const ok = await confirm({
      title: `Delete ${p.name}?`,
      message: `Its ${counts[p.id] || 0} device(s) go back to the router's settings, with no limit.`,
      confirmLabel: "Delete",
      variant: "danger",
    });
    if (ok) await dc.deleteProfile(p.id);
  };
  const add = async (e) => {
    e.preventDefault();
    if (await dc.assign(adding.mac, adding.profile)) setAdding({ mac: "", profile: "" });
  };

  return (
    <div className="dl">
      {dc.error && <div className="dl-error" role="alert">{dc.error}</div>}

      <div className="panel">
        <div className="panel-title">{W.NEW_DEVICES.title}</div>
        <p className="dl-note">{W.NEW_DEVICES.explain}</p>
        <select className="dl-input" value={view.new_devices || ""} disabled={dc.busy}
          aria-label="Profile new devices join" onChange={(e) => joinNew(e.target.value)}>
          <option value="">{W.NEW_DEVICES.none}</option>
          {profiles.map((p) => <option key={p.id} value={p.id}>Join {p.name}: {W.limitHeadline(p).toLowerCase()}</option>)}
        </select>
      </div>

      <div className="panel">
        <div className="dl-head">
          <div className="panel-title">Profiles</div>
          {editing !== "new" && (
            <button type="button" className="dl-btn" onClick={() => setEditing("new")}><FiPlus size={14} /> New profile</button>
          )}
        </div>
        {editing === "new" && (
          <ProfileForm bounds={bounds} lines={lines} classList={classList} busy={dc.busy} onSave={save} onCancel={() => setEditing(null)} />
        )}
        {profiles.length === 0 && editing !== "new" && (
          <p className="dl-note">No profiles yet. Every device uses the router's settings.</p>
        )}
        {profiles.map((p) => (editing === p.id ? (
          <ProfileForm key={p.id} profile={p} bounds={bounds} lines={lines} classList={classList} busy={dc.busy} onSave={save} onCancel={() => setEditing(null)} />
        ) : (
          <div key={p.id} className="dl-row">
            <div className="dl-row-main">
              <strong>{p.name}</strong>
              <span>{W.profileSummary(p, wanName)}</span>
              {W.classSummaries(p, wanName, labelFor).map((line) => <span key={line}>{line}</span>)}
            </div>
            <span className="dl-muted">{counts[p.id] || 0} device(s)</span>
            <button type="button" className="dl-btn icon" aria-label={`Edit ${p.name}`} onClick={() => setEditing(p.id)}><FiEdit2 size={14} /></button>
            <button type="button" className="dl-btn icon danger" aria-label={`Delete ${p.name}`} onClick={() => remove(p)}><FiTrash2 size={14} /></button>
          </div>
        )))}
      </div>

      <div className="panel">
        <div className="panel-title">Devices with a profile</div>
        {members.length === 0 && <p className="dl-note">No device is in a profile yet.</p>}
        {members.map((m) => {
          const p = byId[m.profile];
          const e = entries[m.mac];
          const words = W.capped(p) ? W.enforcementWords(e) : null;
          return (
            <div key={m.mac} className="dl-row">
              <div className="dl-row-main">
                <strong>{names[m.mac] || m.mac}</strong>
                <span>{W.profileSummary(p, wanName)}. {W.limitSource(p, m.joined)} {W.classCountWords(p)}</span>
                {words && <span className={words.ok ? "dl-ok" : "dl-bad"}>{words.label}{words.detail ? `. ${words.detail}` : ""}</span>}
                {(e?.down || e?.up || e?.traffic) && (
                  <details className="dl-measure">
                    <summary>Show the measurements</summary>
                    {e.traffic && <span>{W.usageWords(e.traffic, wanName, fmtBytes)}</span>}
                    {e.down && <span>Download: {fmtBytes(e.down.bytes)} through the limit, {W.heldBy(e.down)}.</span>}
                    {e.up && <span>Upload: {fmtBytes(e.up.bytes)} through the limit, {W.heldBy(e.up)}.</span>}
                  </details>
                )}
              </div>
              {!m.joined && (
                <select className="dl-input narrow" value={m.profile} disabled={dc.busy}
                  aria-label={`Profile for ${names[m.mac] || m.mac}`} onChange={(ev) => dc.assign(m.mac, ev.target.value)}>
                  <option value="">No profile (router's settings)</option>
                  {profiles.map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                </select>
              )}
            </div>
          );
        })}
        {profiles.length > 0 && (
          <form className="dl-add" onSubmit={add}>
            <select className="dl-input" value={adding.mac} aria-label="Device to add"
              onChange={(e) => setAdding({ ...adding, mac: e.target.value })}>
              <option value="">Choose a device on the network…</option>
              {unassigned.map((d) => (
                <option key={d.mac} value={W.normalizeMac(d.mac)}>{names[W.normalizeMac(d.mac)]} ({d.mac})</option>
              ))}
            </select>
            <select className="dl-input" value={adding.profile} aria-label="Profile to add it to"
              onChange={(e) => setAdding({ ...adding, profile: e.target.value })}>
              <option value="">Choose a profile…</option>
              {profiles.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <button type="submit" className="dl-btn primary" disabled={dc.busy || !adding.mac || !adding.profile}>Add</button>
          </form>
        )}
      </div>
    </div>
  );
}
